import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { money, SHOP_INFO, API_BASE } from './api';
import './style.css';

// Klantenscherm (tweede monitor). Fases:
//  welcome  -> begroeting zolang er niets gescand is
//  cart     -> lijst met aankopen + totaal
//  change   -> bij cash: teruggegeven bedrag (3s), daarna thanks
//  thanks   -> "Bedankt voor uw bezoek, tot binnenkort"
// Loopt live mee via BroadcastChannel 'singh-kassa-klant' (zelfde pc, geen server nodig).
function PayLogo({ src, label, cls }) {
  const [err, setErr] = useState(false);
  if (err) return <span className={'paymark ' + cls}>{label}</span>;
  return <img src={src} alt={label} className="paylogo" onError={() => setErr(true)} />;
}

export default function CustomerDisplay() {
  const { shop: shopParam } = useParams();
  const info = (shopParam && SHOP_INFO[shopParam]) || null;
  const [screen, setScreen] = useState({ phase: 'welcome', shopName: info ? info.name : 'Singh Express Shop', shopAddress: info ? info.address : '', items: [], totalCents: 0 });
  const lockRef = useRef(false);
  const timers = useRef([]);
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  useEffect(() => {
    const applyDisplay = (d) => {
      if (lockRef.current) return;
      setScreen({
        phase: d.phase || ((d.items || []).length ? 'cart' : 'welcome'),
        shopName: d.shopName || 'Singh Express Shop',
        shopAddress: d.shopAddress || '',
        items: d.items || [],
        totalCents: d.totalCents || 0,
      });
    };
    const applySaleDone = (d) => {
      clearTimers();
      lockRef.current = true;
      const base = { shopName: d.shopName || 'Singh Express Shop', shopAddress: d.shopAddress || '', items: [] };
      if (d.paymentMethod === 'CASH' && d.changeCents > 0) {
        setScreen({ ...base, phase: 'change', changeCents: d.changeCents, receivedCents: d.receivedCents || 0, totalCents: d.totalCents || 0 });
        timers.current.push(setTimeout(() => setScreen({ ...base, phase: 'thanks' }), 5000));
        timers.current.push(setTimeout(() => { lockRef.current = false; setScreen({ ...base, phase: 'welcome', totalCents: 0 }); }, 10000));
      } else {
        setScreen({ ...base, phase: 'thanks' });
        timers.current.push(setTimeout(() => { lockRef.current = false; setScreen({ ...base, phase: 'welcome', totalCents: 0 }); }, 5000));
      }
    };
    // 1) Zelfde Chrome-profiel: direct via BroadcastChannel
    const ch = new BroadcastChannel(shopParam ? 'singh-kassa-klant-' + shopParam : 'singh-kassa-klant');
    ch.onmessage = (e) => {
      const d = e?.data;
      if (!d) return;
      if (d.type === 'display') applyDisplay(d);
      else if (d.type === 'sale-done') applySaleDone(d);
    };
    ch.postMessage({ type: 'request' });
    // 2) Aparte kiosk-profielen/toestellen: live meelopen via de server (polling)
    let lastEvent = -1, stopped = false, timer = null;
    const poll = async () => {
      try {
        const r = await fetch(API_BASE + '/display/' + (shopParam || ''), { cache: 'no-store' });
        if (r.ok) {
          const j = await r.json();
          if (lastEvent === -1) lastEvent = j.eventId || 0; // bij openen geen oude verkoop herhalen
          if (j.event && (j.eventId || 0) > lastEvent) { lastEvent = j.eventId; applySaleDone(j.event); }
          else if (j.state) applyDisplay(j.state);
        }
      } catch (e) { /* kassa even offline: stil negeren */ }
      if (!stopped) timer = setTimeout(poll, 800);
    };
    timer = setTimeout(poll, 300);
    return () => { ch.close(); clearTimers(); stopped = true; if (timer) clearTimeout(timer); };
  }, []);

  const [fs, setFs] = useState(false);
  const goFullscreen = async () => {
    const el = document.documentElement;
    try {
      let opts;
      if ('getScreenDetails' in window) {
        try {
          const d = await window.getScreenDetails();
          const ext = d.screens.find((s) => !s.isPrimary) || d.currentScreen;
          if (ext) opts = { screen: ext };
        } catch (e) { /* permissie niet verleend */ }
      }
      await el.requestFullscreen(opts);
      setFs(true);
    } catch (e) { /* vereist gebruikersactie: terugvalknop blijft zichtbaar */ }
  };
  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    goFullscreen(); // meteen proberen (activatie overgeërfd van het openen)
    // Garandeer F11 bij de eerste aanraking/toets als auto niet lukte
    const force = () => { if (!document.fullscreenElement) goFullscreen(); };
    window.addEventListener('pointerdown', force);
    window.addEventListener('keydown', force);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      window.removeEventListener('pointerdown', force);
      window.removeEventListener('keydown', force);
    };
  }, []);

  const prevItemsRef = useRef([]);
  const itemsRef = useRef(null);
  const [itemFlash, setItemFlash] = useState({ idx: -1, seq: 0 });
  useEffect(() => {
    if (screen.phase !== 'cart') { prevItemsRef.current = []; return; }
    const prev = prevItemsRef.current || [];
    const next = screen.items || [];
    let idx = -1;
    for (let i = 0; i < next.length; i++) {
      const p = prev.find((x) => x.name === next[i].name);
      if (!p) { idx = i; break; }
      if ((next[i].qty || 0) > (p.qty || 0)) idx = i;
    }
    prevItemsRef.current = next;
    if (idx >= 0) setItemFlash((f) => ({ idx, seq: f.seq + 1 }));
  }, [screen.items, screen.phase]);
  useEffect(() => {
    if (itemFlash.idx < 0 || !itemsRef.current) return;
    const el = itemsRef.current.children[itemFlash.idx];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const t = setTimeout(() => setItemFlash((f) => ({ ...f, idx: -1 })), 1600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFlash.seq]);

  return (
    <div className="klant-screen" data-testid="customer-display">
      {!fs && <button className="klant-fs-btn" data-testid="customer-fullscreen-btn" onClick={goFullscreen} title="Volledig scherm">Volledig scherm</button>}
      <div className="klant-head">
        <div className="klant-mark">S</div>
        <div>
          <div className="klant-shop" data-testid="customer-shop-name">{screen.shopName}</div>
          {screen.shopAddress && <div className="klant-addr" data-testid="customer-shop-address">{screen.shopAddress}</div>}
        </div>
      </div>

      {screen.phase === 'welcome' && (
        <div className="klant-body klant-welcome" data-testid="customer-welcome">
          <div className="klant-welcome-big">Welkom</div>
          <div className="klant-welcome-sub">{screen.shopName}</div>
        </div>
      )}

      {screen.phase === 'cart' && (
        <div className="klant-cart" data-testid="customer-cart">
          <div className="klant-items" ref={itemsRef}>
            {screen.items.map((it, i) => (
              <div className={'klant-item' + (itemFlash.idx === i ? ' klant-item--flash' : '')} key={i}>
                <span className="klant-item-qty">{it.qty}×</span>
                <span className="klant-item-name">{it.name}</span>
                <span className="klant-item-price">{money(it.lineCents)}</span>
              </div>
            ))}
          </div>
          <div className="klant-total">
            <span>Totaal</span>
            <b data-testid="customer-total">{money(screen.totalCents || 0)}</b>
          </div>
        </div>
      )}

      {screen.phase === 'change' && (
        <div className="klant-body klant-change" data-testid="customer-change">
          <div className="klant-label">Terug te geven</div>
          <div className="klant-amount" data-testid="customer-change-amt">{money(screen.changeCents || 0)}</div>
          <div className="klant-change-sub">Ontvangen {money(screen.receivedCents || 0)} · Totaal {money(screen.totalCents || 0)}</div>
        </div>
      )}

      {screen.phase === 'thanks' && (
        <div className="klant-body klant-thanks" data-testid="customer-thanks">
          <div className="klant-thanks-big">Bedankt voor uw bezoek</div>
          <div className="klant-thanks-sub">Tot binnenkort!</div>
        </div>
      )}

      {(screen.phase === 'welcome' || screen.phase === 'cart') && (
        <div className="klant-foot">
          <div className="klant-foot-thanks">Bedankt voor uw bezoek</div>
          <div className="klant-paymarks" data-testid="customer-paymarks">
            <PayLogo src="/paylogos/bancontact.png" label="Bancontact" cls="paymark--bc" />
            <PayLogo src="/paylogos/visa.png" label="Visa" cls="paymark--mo" />
            <PayLogo src="/paylogos/mastercard.png" label="Mastercard" cls="paymark--mc" />
          </div>
        </div>
      )}
    </div>
  );
}
